import type { ValidationReportItem } from '@/domains/report/api/types';

import { computeValueDisplay } from './aggregateValue';

/** UI-only filter modes for the information list. Never affects underlying data. */
export type CriterionFilter = 'all' | 'present' | 'missing' | 'inconsistent';

/** Order used to render filter buttons. */
export const CRITERION_FILTERS: readonly CriterionFilter[] = [
  'all',
  'present',
  'missing',
  'inconsistent',
];

/**
 * Whether an item belongs in the given filter bucket.
 * `present`/`missing` reuse `computeValueDisplay` so the filter matches the
 * label shown on the card. Buckets overlap: an inconsistent item is also present.
 */
export function matchesFilter(item: ValidationReportItem, filter: CriterionFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'present':
      return computeValueDisplay(item).kind !== 'missing';
    case 'missing':
      return computeValueDisplay(item).kind === 'missing';
    case 'inconsistent':
      return item.consistent === false;
  }
}

export function filterCriteria(
  items: ValidationReportItem[],
  filter: CriterionFilter,
): ValidationReportItem[] {
  return items.filter((item) => matchesFilter(item, filter));
}

/** Count of items in each bucket, computed from the full unfiltered list. */
export function countByFilter(
  items: ValidationReportItem[],
): Record<CriterionFilter, number> {
  return {
    all: items.length,
    present: items.filter((item) => matchesFilter(item, 'present')).length,
    missing: items.filter((item) => matchesFilter(item, 'missing')).length,
    inconsistent: items.filter((item) => matchesFilter(item, 'inconsistent')).length,
  };
}
